import { useCallback } from 'react';
import { useLogs, useUI } from '../contexts';

const TASK_ENDPOINT = process.env.REACT_APP_TASK_ENDPOINT || '';

type TaskType = 'lint' | 'test' | 'build';

type UseTaskRunnerParams = {
  activeProject: string;
  token: string | null;
  apiBase: string;
  authFetch: (url: string, options?: RequestInit) => Promise<Response>;
};

function summarizeLint(output: unknown): { errors: number; warnings: number } {
  const lines: string[] = Array.isArray(output)
    ? output.map((l) => String(l))
    : typeof output === 'string'
      ? output.split('\n')
      : [];
  let errors = 0;
  let warnings = 0;
  lines.forEach((line) => {
    const lower = line.toLowerCase();
    if (lower.includes('error')) errors += 1;
    else if (lower.includes('warn')) warnings += 1;
  });
  return { errors, warnings };
}

export function useTaskRunner({
  activeProject,
  token,
  apiBase,
  authFetch,
}: UseTaskRunnerParams) {
  const { addLog, setLintSummary } = useLogs();
  const { setStatus, setTaskRunning, setServerStateOverride } = useUI();

  const hasTaskEndpoint = Boolean(TASK_ENDPOINT);

  const runTask = useCallback(
    async (task: TaskType): Promise<boolean> => {
      if (!token || !apiBase) {
        addLog({ task, level: 'error', message: 'Token veya API base yok' });
        setStatus(`${task} · token yok`);
        return false;
      }
      if (!TASK_ENDPOINT) {
        addLog({ task, level: 'error', message: 'TASK_ENDPOINT tanımsız, runner kullanılamıyor' });
        setStatus(`${task} · endpoint yok`);
        return false;
      }

      if (task === 'lint') {
        setLintSummary({ errors: 0, warnings: 0 });
      }
      setStatus(`${task} · running…`);
      setTaskRunning(task);
      addLog({ task, level: 'info', message: `${task} started` });

      try {
        const res = await authFetch(TASK_ENDPOINT, {
          method: 'POST',
          body: JSON.stringify({ projectID: activeProject, task }),
        });
        const data = await res.json();
        console.log('run-task response', data);

        if (data?.status) {
          setServerStateOverride('ok');
          const output =
            data.result?.logs ||
            data.result?.output ||
            data.result?.stdout ||
            data.result?.messages ||
            data.result ||
            [];

          if (Array.isArray(output)) {
            output.forEach((line: string) => addLog({ task, level: 'info', message: line }));
          } else if (typeof output === 'string') {
            output.split('\n').forEach((line: string) => addLog({ task, level: 'info', message: line }));
          } else {
            addLog({ task, level: 'info', message: JSON.stringify(output) });
          }

          addLog({ task, level: 'success', message: `${task} completed` });
          if (task === 'lint') {
            const summary = summarizeLint(output);
            setLintSummary(summary);
            setStatus(`Lint: ${summary.errors} errors, ${summary.warnings} warnings`);
          } else {
            setStatus(`${task} · completed`);
          }
          setTaskRunning(null);
          return true;
        }

        addLog({ task, level: 'error', message: `${task} failed`, details: [JSON.stringify(data)] });
        setServerStateOverride('error');
        if (task === 'lint') {
          const summary = summarizeLint(data?.result || data);
          setLintSummary(summary);
          setStatus(`Lint: ${summary.errors} errors, ${summary.warnings} warnings`);
        } else {
          setStatus(`${task} · failed`);
        }
        setTaskRunning(null);
        return false;
      } catch (error: unknown) {
        const msg = error instanceof Error ? error.message : 'Unknown error';
        addLog({ task, level: 'error', message: `${task} error`, details: [msg] });
        setServerStateOverride('error');
        if (task === 'lint') {
          setLintSummary({ errors: 1, warnings: 0 });
          setStatus('Lint: 1 errors, 0 warnings');
        } else {
          setStatus(`${task} · error`);
        }
        setTaskRunning(null);
        return false;
      }
    },
    [activeProject, addLog, authFetch, apiBase, token, setLintSummary, setServerStateOverride, setStatus, setTaskRunning]
  );

  const handleLint = useCallback(() => runTask('lint'), [runTask]);
  const handleTest = useCallback(() => runTask('test'), [runTask]);
  const handleBuild = useCallback(() => runTask('build'), [runTask]);
  const handleRunAll = useCallback(async () => {
    await runTask('lint');
    await runTask('test');
    await runTask('build');
  }, [runTask]);

  return {
    hasTaskEndpoint,
    runTask,
    handleLint,
    handleTest,
    handleBuild,
    handleRunAll,
  };
}
